import React, { useCallback, useEffect, useState } from 'react';
import {
    CATEGORY_LEGAL_ANNOUCEMENT,
    useSelectedDossierContext,
    VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { useTheme } from '@mui/material';
import {
    ArticleValidationProcessStatus,
    DossierArticle,
    UpdateArticleParamsType,
    useDossierArticle,
    useDossierLegalAnnoucements,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '../../hooks';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierLegalAnnoucementsFooter from '../DossierLegalAnnoucementsFooter/DossierLegalAnnoucementsFooter';
import AddLegalAnnoucementModal from '../AddLegalAnnoucementModal/AddLegalAnnoucementModal';
import { AddLegalAnnoucementFormData } from '../AddLegalAnnoucementForm/AddLegalAnnoucementForm';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import EditLegalAnnoucementForm, {
    EditLegalAnnoucementFormFormData,
} from '../EditLegalAnnoucementForm/EditLegalAnnoucementForm';
import SelectLegalAnnoucementForm from '../SelectLegalAnnoucementForm/SelectLegalAnnoucementForm';
import DossierArticlesProcessesList from '../DossierArticlesProcessesList/DossierArticlesProcessesList';

const DossierLegalAnnoucements: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_LEGAL_ANNOUCEMENT;
    const { dossier, refreshDossier } = useSelectedDossierContext();
    const [processing, setProcessing] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucement>>([]);
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>(true);
    const { prestationArticlesApi } = useFormaliteService();
    const {
        getProcessesByArticle,
        createValidatorProcess,
        canCreateValidatorProcess,
        findProcessByArticle,
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
    } = useDossierProcesses();

    const {
        handleDeleteArticle,
        handlePublishLegalAnnoucement,
        loadLegalAnnoucements,
        actionShowLegalAnouncement,
        actionRemoveLinkedLegalAnouncement,
        actionDeleteArticlePresta,
        actionAttachLegalAnouncement,
        actionPublishLegalAnouncement,
    } = useDossierProcessesActionsAndRender();

    const { addLegalAnnoucement, deleteLegalAnnoucement, getLegalAnnoucementTypes } =
        useDossierLegalAnnoucements();
    const { updateArticle } = useDossierArticle();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { palette } = useTheme();

    const handleAttachLegalAnnoucement = async (idLegalAnnoucement: number, articleId: number) => {
        if (!dossier.id) {
            return;
        }

        setProcessing(true);

        try {
            const process = findProcessByArticle(dossier, [validatorTypeId], articleId);
            if (!process) {
                throw new Error("Aucun processus de validation en cours sur l'article");
            }

            const request: PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest =
                {
                    id: articleId,
                    apiArticlesPrestationIdAttacherAnnonceLegalePatchRequest: {
                        idAnnonceLegale: idLegalAnnoucement,
                    },
                };

            const response =
                await prestationArticlesApi.apiArticlesPrestationIdAttacherAnnonceLegalePatch(
                    request,
                );

            if (response.status === 200) {
                modalActions.close();
                enqueueSnackbar('Votre annonce a bien été rattachée');
                refreshDossier();
                setLegalAnnoucementsLoaded(false);
            } else {
                enqueueSnackbar(
                    'Une erreur est survenue durant le rattachement de votre annonce légale',
                    { variant: 'error' },
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                'Une erreur est survenue durant le rattachement de votre annonce légale',
                { variant: 'error' },
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseModal = () => {
        modalActions.close();
    };

    const handleDeleteLegalAnnoucement = async (article: DossierArticle) => {
        const { status } = await deleteLegalAnnoucement(
            article,
            dossier,
            validatorTypeId,
            'Le rattachement a bien été supprimé',
            'Une erreur est survenue durant la suppression du rattachement',
            'to-link',
        );

        if (status === 200) {
            refreshDossier();
            setLegalAnnoucementsLoaded(false);
        }

        handleCloseModal();
    };

    const handleAddLegalAnnoucement = async (data: AddLegalAnnoucementFormData) => {
        setProcessing(true);

        try {
            const articleId = await addLegalAnnoucement(
                data.title,
                data.prestationId as number,
                data.typeAnnonce as number,
            );
            await createValidatorProcess(dossier.id as number, [articleId]);
            enqueueSnackbar('Votre annonce a bien été ajoutée');
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de votre annonce",
            );
        } finally {
            setProcessing(false);
            setShowAddModal(false);
            refreshDossier();
        }
    };

    const onSubmitEdiLegalAnnoucement = async (data: EditLegalAnnoucementFormFormData) => {
        setProcessing(true);

        try {
            await updateArticle({
                prestaArticleId: data.prestaArticleId,
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
                typeArticleId: data.typeAnnonce,
            } as UpdateArticleParamsType);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'édition de la démarche",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleEditLegalAnnoucement = useCallback(
        async (prestaArticle: DossierArticle) => {
            const results = await getLegalAnnoucementTypes();
            const legalAnnoucementTypes = results || [];
            const currentType = results.find((legal) => legal.id === prestaArticle.idStype);

            modalActions.call(
                <EditLegalAnnoucementForm
                    onCancel={() => {
                        setProcessing(false);
                        modalActions.close();
                    }}
                    prestaArticle={prestaArticle}
                    onSubmit={onSubmitEdiLegalAnnoucement}
                    dossier={dossier}
                    legalAnnoucementTypes={legalAnnoucementTypes}
                    currentType={currentType}
                />,
            );
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier, modalActions, processing],
    );

    const getItemActions = useCallback(
        (item: DossierArticle) => {
            let actions: Array<DossierArticleProcessMenuItemProps> = [];
            let status: ArticleValidationProcessStatus = 'to-link';
            const processes = getProcessesByArticle(dossier, validatorTypeId, item.id);

            if (processes.length > 0) {
                status = processes[0].statusCode as ArticleValidationProcessStatus;
            }

            if (
                ['to-link'].includes(status) &&
                canCreateValidatorProcess(dossier, validatorTypeId)
            ) {
                actions = [
                    ...actions,
                    actionAttachLegalAnouncement(() => {
                        modalActions.call(
                            <SelectLegalAnnoucementForm
                                processing={processing}
                                onItemSelected={handleAttachLegalAnnoucement}
                                article={item}
                                sTypeId={item.idStype}
                                title="Rechercher une annonce légale"
                                placeholder="Rechercher une annonce légale"
                            />,
                        );
                    }),
                    {
                        label: 'Modifier la démarche',
                        icon: 'edit' as IconName,
                        onClick: () => handleEditLegalAnnoucement(item),
                    },
                    actionDeleteArticlePresta(
                        () =>
                            handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                        () => modalActions.close(),
                    ),
                ];
            } else if (['ongoing', 'default', 'rescheduled', 'validate'].includes(status)) {
                actions = [
                    ...actions,
                    actionShowLegalAnouncement(
                        item.id,
                        dossier,
                        validatorTypeId,
                        legalAnnoucements,
                    ),
                    actionPublishLegalAnouncement(
                        () =>
                            handlePublishLegalAnnoucement(
                                item,
                                dossier,
                                validatorTypeId,
                                () => {
                                    setLegalAnnoucementsLoaded(false);
                                    refreshDossier();
                                },
                                () => handleCloseModal(),
                            ),
                        () => handleCloseModal,
                    ),
                    actionRemoveLinkedLegalAnouncement(
                        handleDeleteLegalAnnoucement,
                        handleCloseModal,
                    ),
                ];
            } else if (['failed'].includes(status)) {
                actionDeleteArticlePresta(
                    () => handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                    () => modalActions.close(),
                );
            }

            return actions;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canCreateValidatorProcess,
            dossier,
            getProcessesByArticle,
            legalAnnoucements,
            validatorTypeId,
        ],
    );

    const status = getArticleProcessesStatus(
        dossier,
        validatorTypeId,
        articles.map((article) => article.id),
    );

    useEffect(() => {
        if (!dossier.id) {
            return;
        }

        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);
            loadLegalAnnoucements(dossier, CATEGORY_LEGAL_ANNOUCEMENT, [validatorTypeId])
                .then((items) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    setLegalAnnoucements([]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    useEffect(() => {
        setLegalAnnoucementsLoaded(false);
    }, [dossier.id]);

    return (
        <>
            <Accordion
                title="Annonces légales"
                expandLabel={getArticleProcessesStatusLabel(status)}
                expanded
                expandColor={getArticleProcessesStatusColor(status)}
                sx={{ backgroundColor: palette.background.paper }}
                contentSxProps={{ gap: '16px', display: 'grid' }}
            >
                <DossierArticlesProcessesList
                    articles={articles}
                    dossier={dossier}
                    validatorTypeId={validatorTypeId}
                    legalAnnoucements={legalAnnoucements}
                    getItemActions={getItemActions}
                />
                <DossierLegalAnnoucementsFooter
                    canAddLegalAnnoucement={canCreateValidatorProcess(dossier, validatorTypeId)}
                    handleAddLegalAnnoucement={() => setShowAddModal(true)}
                />
            </Accordion>
            <AddLegalAnnoucementModal
                showModal={showAddModal}
                processing={processing}
                onClose={() => setShowAddModal(false)}
                onSubmit={handleAddLegalAnnoucement}
            />
        </>
    );
};

export default DossierLegalAnnoucements;
